import React from 'react';

import { FEED_PREFERENCES } from '../../components/FeedPreferenceTabMenu';
import { FeedFilters, loadFeedFilters } from '../../helpers';
import { Video } from '../../types';

interface State {
  videos: Video[];
  // Position of the video on screen.
  index: number;
  loading: boolean;
  // No more page to ask.
  ended: boolean;
  // Tab of the feed (forYou, following, ranking).
  preference: string;
  filters: FeedFilters;
  commentsOpen: boolean;
  // Video of a shared link (/short/:codeshare), kept first when a new feed comes.
  sharedId: string;
}

type Action =
  | { type: 'pageRequested' }
  | { type: 'feedRestarted' }
  | { type: 'feedReplaced'; docs: Video[]; keepId: string; linkedId: string; ended: boolean }
  | { type: 'profileVideosReplaced'; videos: Video[]; index: number; ended: boolean }
  | { type: 'videosAppended'; docs: Video[]; ended: boolean }
  | { type: 'videosUpdated'; update: React.SetStateAction<Video[]> }
  | { type: 'deepLinkVideoPinned'; video: Video; openComments: boolean }
  | { type: 'sharedVideoPinned'; video: Video }
  | { type: 'sharedLinkLeft' }
  | { type: 'moved'; delta: number }
  | { type: 'commentsToggled' }
  | { type: 'commentsClosed' }
  | { type: 'preferenceChanged'; preference: string }
  | { type: 'filtersChanged'; filters: FeedFilters };

// Same preference as the app, saved on the user; the filters come from the storage.
const createInitialState = (feedPreference?: string): State => ({
  videos: [],
  index: 0,
  loading: true,
  ended: false,
  preference: FEED_PREFERENCES.includes(feedPreference || '') ? feedPreference as string : 'forYou',
  filters: loadFeedFilters(),
  commentsOpen: false,
  sharedId: '',
});

// The videos of the page not already in the list.
const appendNew = (videos: Video[], docs: Video[]) => {
  const ids = videos.map((v) => v.id);
  return [...videos, ...docs.filter((v) => !ids.includes(v.id))];
};

// The video goes first, the rest of the list follows it.
const pinFirst = (videos: Video[], video: Video) => [video, ...videos.filter((v) => v.id !== video.id)];

const feedReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'pageRequested':
      return { ...state, loading: true };
    case 'feedRestarted':
      // A new feed (tab, filters, login): the current video stays on screen until the new one comes.
      return { ...state, ended: false, commentsOpen: false };
    case 'feedReplaced': {
      // First page of a new feed: the video of the link, or the one on screen when the page has it too, stays first.
      const {
        docs,
        keepId,
        linkedId,
        ended,
      } = action;
      const kept = state.videos.filter((v) => v.id === keepId && (v.id === linkedId || docs.some((d) => d.id === v.id)));
      return {
        ...state,
        videos: [...kept, ...docs.filter((v) => v.id !== keepId)],
        index: 0,
        ended,
        loading: false,
      };
    }
    case 'profileVideosReplaced': {
      // First page of the videos of a profile: it starts on the video clicked.
      const { videos, index, ended } = action;
      return {
        ...state,
        videos,
        index,
        ended,
        loading: false,
      };
    }
    case 'videosAppended': {
      // Next page: after the list, without the videos already known.
      const { docs, ended } = action;
      return {
        ...state,
        videos: appendNew(state.videos, docs),
        ended,
        loading: false,
      };
    }
    case 'videosUpdated': {
      // A change of the actions (like, view...): a setState-like update of the list.
      const { update } = action;
      const videos = typeof update === 'function' ? update(state.videos) : update;
      return { ...state, videos };
    }
    case 'deepLinkVideoPinned': {
      // The video of a notification or a profile goes first; its comments open when one is asked.
      const { video, openComments } = action;
      return {
        ...state,
        videos: pinFirst(state.videos, video),
        index: 0,
        commentsOpen: openComments ? true : state.commentsOpen,
      };
    }
    case 'sharedVideoPinned': {
      // The video of a shared link goes first and is remembered for the next feeds.
      const { video } = action;
      return {
        ...state,
        sharedId: video.id,
        videos: pinFirst(state.videos, video),
        index: 0,
      };
    }
    case 'sharedLinkLeft':
      return { ...state, sharedId: '' };
    case 'moved': {
      // Previous or next video, inside the list; the comments belong to a video, they close.
      const last = Math.max(state.videos.length - 1, 0);
      const index = Math.min(Math.max(state.index + action.delta, 0), last);
      return { ...state, index, commentsOpen: false };
    }
    case 'commentsToggled':
      return { ...state, commentsOpen: !state.commentsOpen };
    case 'commentsClosed':
      return { ...state, commentsOpen: false };
    case 'preferenceChanged':
      return { ...state, preference: action.preference };
    case 'filtersChanged':
      return { ...state, filters: action.filters };
    default:
      return state;
  }
};

export type { Action, State };

export { createInitialState, feedReducer };
